import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './NoteModal.module.css';
import { CloseIcon, HeartIcon } from '../../Icons';
import { API_BASE_URL } from '../../../services/api';
import api from '../../../services/api';

const AI_TABS = [
  {
    key: 'sentenceAnalysis',
    label: '句子解析',
    prompt: '请像英文老师讲解句子一样，针对上述英文内容进行解析：1）列出关键短语（动词短语、固定搭配、介词短语等），每条使用“短语 — 中文解释 — 在句子中的作用 — 常用例句”的格式，例句需与原句不同；2）若出现特别特俗的固定搭配（如 not only ... but also ...），额外说明其使用规则。不需要分析主语/谓语/状语等语法成分，也不需要学习建议；无法解析时请输出空。'
  },
  {
    key: 'keywords',
    label: '重点单词',
    prompt: '仅输出重点单词列表，每行严格使用 - 单词 中文解释 + 根据单词新造的例句 的格式。禁止输出任何分析、思考、说明、总结、前言或后语；如无法遵守请直接输出空。'
  },
  {
    key: 'phrases',
    label: '短语列表',
    prompt: '仅输出关键短语列表，每行严格使用 - 短语 中文解释 + 根据短语举一反三的例句 的格式。禁止输出任何分析或多余文字；若无内容请输出空。'
  },
  {
    key: 'grammar',
    label: '语法分析',
    prompt: '仅输出语法要点列表，每行严格使用 - 语法点：简要说明 的格式。禁止输出分析过程或额外文字；若无内容请输出空。'
  }
];

const NoteModal = ({
  visible,
  sentenceIndex = null,
  originalText = '',
  translatedText = '',
  value,
  loading = false,
  error = '',
  onChange,
  onClose,
  onSubmit,
  novelId,
  chapterId,
  volumeId = null,
  initialAiNote = null,
}) => {
  const tabConfigs = useMemo(() => AI_TABS, []);

  const createEmptyResponses = useCallback(() => {
    const empty = {};
    tabConfigs.forEach((tab) => {
      empty[tab.key] = '';
    });
    return empty;
  }, [tabConfigs]);

  const [activeTab, setActiveTab] = useState('ai');
  const [activeAiTab, setActiveAiTab] = useState(tabConfigs[0].key);
  const [aiResponses, setAiResponses] = useState(createEmptyResponses);
  const [aiStreaming, setAiStreaming] = useState(false);
  const [aiStreamingTab, setAiStreamingTab] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiSaveStatus, setAiSaveStatus] = useState({});
  const [aiManualStopFlags, setAiManualStopFlags] = useState({});

  const controllerRef = useRef(null);

  const extractAiNoteSegments = useCallback(
    (note) => {
      const responses = createEmptyResponses();

      if (!note || typeof note !== 'object') {
        return responses;
      }

      const toText = (value) => {
        if (value === null || value === undefined) {
          return '';
        }
        if (typeof value === 'string') {
          return value.trim();
        }
        if (Array.isArray(value)) {
          return value
            .map((item) => toText(item))
            .filter(Boolean)
            .join('\n');
        }
        if (typeof value === 'object') {
          const candidates = [
            value.text,
            value.content,
            value.value,
            value.summary,
            value.description,
            value.note,
            value.body,
            value.message,
          ];
          for (const candidate of candidates) {
            const text = toText(candidate);
            if (text) {
              return text;
            }
          }

          if (Array.isArray(value.lines)) {
            const joined = value.lines
              .map((line) => toText(line))
              .filter(Boolean)
              .join('\n');
            if (joined) {
              return joined;
            }
          }

          if (Array.isArray(value.items)) {
            const joined = value.items
              .map((item) => toText(item))
              .filter(Boolean)
              .join('\n');
            if (joined) {
              return joined;
            }
          }

          try {
            return JSON.stringify(value);
          } catch (err) {
            console.warn('无法序列化 AI 笔记字段:', err);
            return '';
          }
        }
        return String(value).trim();
      };

      const formatKeywordEntries = (value) => {
        if (!value) {
          return '';
        }
        if (typeof value === 'string') {
          return value.trim();
        }
        if (Array.isArray(value)) {
          return value
            .map((entry) => {
              if (!entry) {
                return '';
              }
              if (typeof entry === 'string') {
                return entry.trim();
              }
              if (typeof entry === 'object') {
                const keyword = entry.word ?? entry.keyword ?? entry.text ?? '';
                const meaning = entry.meaning ?? entry.translation ?? entry.explanation ?? '';
                const example = entry.example ?? entry.example_en ?? entry.exampleEn ?? '';
                const exampleCn =
                  entry.example_cn ??
                  entry.exampleCn ??
                  entry.translation ??
                  entry.example_translation ??
                  entry.exampleTranslation ??
                  '';

                const lines = [];
                const header = [keyword, meaning].filter(Boolean).join(' — ').trim();
                if (header) {
                  lines.push(`- ${header}`);
                }
                if (example) {
                  lines.push(`例句：${example}`);
                }
                if (exampleCn && exampleCn !== example) {
                  lines.push(exampleCn);
                }
                return lines.join('\n').trim();
              }
              return String(entry).trim();
            })
            .filter(Boolean)
            .join('\n\n');
        }
        if (typeof value === 'object') {
          return toText(value);
        }
        return String(value).trim();
      };

      const formatPhraseEntries = (value) => {
        if (!value) {
          return '';
        }

        const entries = Array.isArray(value) ? value : [value];
        return entries
          .map((entry) => {
            if (!entry) {
              return '';
            }
            if (typeof entry === 'string') {
              return entry.trim();
            }
            if (typeof entry === 'object') {
              const phraseText = entry.phrase ?? entry.text ?? entry.expression ?? '';
              const meaning = entry.meaning ?? entry.translation ?? entry.explanation ?? '';
              const usageNote = entry.usage_note ?? entry.usageNote ?? entry.note ?? '';
              const exampleEn = entry.example_en ?? entry.exampleEn ?? entry.example ?? '';
              const exampleCn =
                entry.example_cn ??
                entry.exampleCn ??
                entry.example_translation ??
                entry.exampleTranslation ??
                '';

              const lines = [];
              const title = [phraseText, meaning].filter(Boolean).join('：').trim();
              if (title) {
                lines.push(title);
              }
              if (usageNote) {
                lines.push(`用法：${usageNote}`);
              }
              if (exampleEn) {
                lines.push(`例句：${exampleEn}`);
              }
              if (exampleCn) {
                lines.push(exampleCn);
              }
              return lines.join('\n').trim();
            }
            return String(entry).trim();
          })
          .filter(Boolean)
          .join('\n\n');
      };

      const assignText = (key, value) => {
        const text = toText(value);
        if (text) {
          responses[key] = text;
        }
      };

      assignText(
        'sentenceAnalysis',
        note.analysis ??
          note.sentence_analysis ??
          note.sentenceAnalysis ??
          note.summary ??
          note.summary_text ??
          note.summaryText ??
          note.description ??
          note.analysis_text ??
          note.analysisText ??
          note.ai_summary ??
          note.aiSummary ??
          ''
      );

      const keywordsSource =
        note.keywords ??
        note.key_words ??
        note.keyword_list ??
        note.keywordList ??
        note.keywords_list ??
        note.keywordsText ??
        note.keywords_text ??
        note.keywordsSummary ??
        note.keywords_summary ??
        null;
      const formattedKeywords = formatKeywordEntries(keywordsSource);
      if (formattedKeywords) {
        responses.keywords = formattedKeywords;
      }

      const phrasesSource =
        note.key_phrases ??
        note.keyPhrases ??
        note.phrases ??
        note.phrase_list ??
        note.phraseList ??
        note.keyPhraseList ??
        note.phraseSummary ??
        note.phrase_summary ??
        null;
      const formattedPhrases = formatPhraseEntries(phrasesSource);
      if (formattedPhrases) {
        responses.phrases = formattedPhrases;
      }

      assignText(
        'grammar',
        note.grammar ??
          note.grammar_points ??
          note.grammarPoints ??
          note.grammar_notes ??
          note.grammarNotes ??
          note.grammar_summary ??
          note.grammarSummary ??
          ''
      );

      return responses;
    },
    [createEmptyResponses]
  );

  useEffect(() => {
    if (visible) {
      const initialResponses = extractAiNoteSegments(initialAiNote);
      const firstTabWithContent = tabConfigs.find((tab) => {
        const value = initialResponses[tab.key];
        return typeof value === 'string' && value.trim().length > 0;
      });

      setActiveTab('ai');
      setActiveAiTab(firstTabWithContent?.key ?? tabConfigs[0].key);
      setAiResponses(initialResponses);
      setAiError('');
      setAiSaveStatus({});
      setAiStreaming(false);
      setAiStreamingTab(null);
      setAiManualStopFlags({});
    } else {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
      setAiStreaming(false);
      setAiStreamingTab(null);
      setAiManualStopFlags({});
    }
  }, [
    visible,
    sentenceIndex,
    originalText,
    translatedText,
    tabConfigs,
    extractAiNoteSegments,
    initialAiNote,
  ]);

  const formatWithBold = useCallback((text) => {
    if (!text) return text;
    return text
      .split(/(\*\*[\s\S]*?\*\*)/g)
      .filter(Boolean)
      .map((segment, idx) => {
        if (segment.startsWith('**') && segment.endsWith('**')) {
          const inner = segment.slice(2, -2);
          return (
            <strong key={`bold-${idx}`} className={styles.aiEmphasis}>
              {inner}
            </strong>
          );
        }
        return <React.Fragment key={`text-${idx}`}>{segment}</React.Fragment>;
      });
  }, []);

  const stopStreaming = useCallback((manual = true) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (manual && aiStreamingTab) {
      setAiManualStopFlags((prev) => ({ ...prev, [aiStreamingTab]: true }));
    }
    setAiStreaming(false);
    setAiStreamingTab(null);
  }, [aiStreamingTab]);

  const fetchAiContent = useCallback(async (tabKey, force = false) => {
    if (!translatedText && !force) {
      // 没有英文上下文时仍允许触发，但提示用户
    }

    setAiError('');

    if (!force && aiResponses[tabKey]) {
      return;
    }

    stopStreaming(false);

    setAiManualStopFlags((prev) => ({ ...prev, [tabKey]: false }));

    const controller = new AbortController();
    controllerRef.current = controller;

    setAiResponses((prev) => ({ ...prev, [tabKey]: '' }));
    setAiStreaming(true);
    setAiStreamingTab(tabKey);

    const tabConfig = tabConfigs.find((tab) => tab.key === tabKey) || tabConfigs[0];

    const contextLines = [];
    if (translatedText) {
      contextLines.push(`英文内容：${translatedText.trim().slice(0, 800)}`);
    }
    // if (originalText) {
    //   contextLines.push(`原文内容：${originalText.trim().slice(0, 800)}`);
    // }

    const systemPrompt = `你是一名英文老师，请根据以下英文句子提取英文知识点，\n${contextLines.join('\n')}不需要分析故事背景，直接给出列点／条目符号形式中文作答，不需要给出思考过程，控制在200字内。`;

    const requestMessages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: tabConfig.prompt }
    ];

    try {
      const response = await fetch(`${API_BASE_URL}/api/guiji-llm/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ messages: requestMessages }),
        signal: controller.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || '调用大模型接口失败');
      }

      if (!response.body) {
        throw new Error('浏览器不支持流式响应');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let doneStreaming = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const segments = buffer.split('\n\n');
        buffer = segments.pop() ?? '';

        for (const segment of segments) {
          const trimmedSegment = segment.trim();
          if (!trimmedSegment) continue;

          const lines = trimmedSegment.split('\n');

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line || !line.startsWith('data:')) continue;

            const content = line.slice(5).trim();
            if (!content) continue;

            if (content === '[DONE]') {
              doneStreaming = true;
              break;
            }

            try {
              const parsed = JSON.parse(content);
              const delta = parsed?.choices?.[0]?.delta?.content || '';
              if (delta) {
                setAiResponses((prev) => ({
                  ...prev,
                  [tabKey]: `${prev[tabKey] || ''}${delta}`
                }));
              }
            } catch (err) {
              console.error('解析流式响应失败:', err, content);
            }
          }

          if (doneStreaming) break;
        }

        if (doneStreaming) break;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('AI请求失败:', error);
      setAiError(typeof error.message === 'string' ? error.message : 'AI请求失败，请稍后重试');
    } finally {
      setAiStreaming(false);
      setAiStreamingTab(null);
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    }
  }, [tabConfigs, translatedText, originalText, aiResponses, stopStreaming]);

  const handleAiTabSelect = (tabKey) => {
    if (activeAiTab === tabKey) {
      return;
    }
    setActiveAiTab(tabKey);
  };

  const handleSaveAiResponse = async (tabKey) => {
    const content = aiResponses[tabKey];
    if (!content || !content.trim()) {
      return;
    }

    if (!chapterId || !novelId) {
      setAiError('缺少章节或小说信息，无法保存AI笔记');
      return;
    }

    setAiSaveStatus((prev) => ({ ...prev, [tabKey]: 'loading' }));

    try {
      await api.post('/user-notes/', {
        note_content: content.trim(),
        sentence_id: typeof sentenceIndex === 'number' ? sentenceIndex : 0,
        chapter_id: chapterId,
        novel_id: novelId,
        volume_id: volumeId,
      });

      setAiSaveStatus((prev) => ({ ...prev, [tabKey]: 'success' }));
    } catch (err) {
      console.error('保存AI回复失败:', err);
      setAiSaveStatus((prev) => ({ ...prev, [tabKey]: 'error' }));

      if (err.response?.status === 401) {
        alert('请先登录后再收藏AI回复');
      } else {
        setAiError('保存AI回复失败，请稍后重试');
      }
    }
  };

  if (!visible) {
    return null;
  }

  const currentAiResponse = aiResponses[activeAiTab] || '';
  const currentAiSaveStatus = aiSaveStatus[activeAiTab];

  const modalContent = (
    <div
      className={styles.overlay}
      onClick={() => {
        if (!loading) {
          onClose?.();
        }
      }}
      role="presentation"
    >
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          <h3 className={styles.title}>添加笔记</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => {
              if (!loading) {
                onClose?.();
              }
            }}
            disabled={loading}
          >
            <CloseIcon size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.tabHeader}>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'ai' ? styles.tabButtonActive : ''}`}
              onClick={() => {
                setActiveTab('ai');
              }}
            >
              AI 笔记
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'manual' ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab('manual')}
            >
              手写笔记
            </button>
          </div>

          {activeTab === 'ai' ? (
            <div className={styles.tabContent}>
              <div className={styles.aiSection}>
                <div className={styles.aiHeader}>
                  <h4>AI助手</h4>
                  <div className={styles.aiActions}>
                    {aiStreaming && aiStreamingTab === activeAiTab && (
                      <span className={styles.aiStatus}>生成中...</span>
                    )}
                    <button
                      type="button"
                      className={styles.aiStopButton}
                      onClick={() => stopStreaming(true)}
                      disabled={!aiStreaming}
                    >
                      停止
                    </button>
                  </div>
                </div>

                <div className={styles.aiSubTabs}>
                  {tabConfigs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`${styles.aiSubTabButton} ${activeAiTab === tab.key ? styles.aiSubTabButtonActive : ''}`}
                      onClick={() => handleAiTabSelect(tab.key)}
                      disabled={aiStreaming && aiStreamingTab !== tab.key}
                    >
                      {tab.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={styles.aiRefreshButton}
                    onClick={() => fetchAiContent(activeAiTab, true)}
                    disabled={aiStreaming}
                  >
                    重新生成
                  </button>
                </div>

                <div className={styles.aiContentBox}>
                  {aiStreaming && aiStreamingTab === activeAiTab && (
                    <div className={styles.aiGenerating}>AI 正在生成中...</div>
                  )}
                  {currentAiResponse ? (
                    <div className={styles.aiContent}>{formatWithBold(currentAiResponse)}</div>
                  ) : (
                    !aiStreaming && (
                      <div className={styles.aiPlaceholder}>点击重新生成按钮获取AI笔记</div>
                    )
                  )}
                </div>

                {aiError && <div className={styles.aiError}>{aiError}</div>}

                <div className={styles.aiMessageActions}>
                  <button
                    type="button"
                    className={`${styles.aiSaveButton} ${currentAiSaveStatus === 'success' ? styles.aiSaveButtonActive : ''}`}
                    onClick={() => handleSaveAiResponse(activeAiTab)}
                    disabled={aiStreaming || !currentAiResponse.trim()}
                    title={currentAiSaveStatus === 'success' ? '已收藏' : '收藏AI笔记'}
                    aria-label={currentAiSaveStatus === 'success' ? '已收藏' : '收藏AI笔记'}
                  >
                    <HeartIcon
                      size={16}
                      filled={currentAiSaveStatus === 'success'}
                    />
                  </button>
                  {currentAiSaveStatus === 'error' && (
                    <span className={`${styles.aiSaveStatus} ${styles.aiSaveStatusError}`}>保存失败</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.tabContent}>
              <div className={styles.noteSection}>
                <label className={styles.inputLabel} htmlFor="note-content">
                  笔记内容
                </label>
                <textarea
                  id="note-content"
                  className={styles.textarea}
                  placeholder="写下你对这段内容的想法或标记..."
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  rows={6}
                  disabled={loading}
                  autoFocus
                />
                {error && <div className={styles.error}>{error}</div>}
              </div>
            </div>
          )}
        </div>

        {activeTab === 'manual' && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.submitButton}
              onClick={onSubmit}
              disabled={loading || !value.trim()}
            >
              {loading ? '保存中...' : '保存笔记'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default NoteModal;
